import { RealtimeClient } from '@openai/realtime-api-beta';
import { ItemType } from '@openai/realtime-api-beta/dist/lib/client.js';
import { AnimatePresence } from 'framer-motion';
import { Eye } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import filterInvestments, { filterProjects } from 'src/tools/filter.js';
import InvestmentPlans from '../components/InvestmentPlans/InvestmentPlans';
import PropertySection from '../components/PropertySection/PropertySection';
import RecordingSection from '../components/RecordingSection/RecordingSection';
import { ProjectType, RealtimeEvent } from '../interfaces';
import { WavRecorder, WavStreamPlayer } from '../lib/wavtools';
import { registerGetInvestmentTool } from '../tools/aiTools/getInvestmentTool';
import { registerGetPropertyTool } from '../tools/aiTools/getPropertyTool';
import { registerManagePropertyListingTool } from '../tools/aiTools/uploadPropertyListingTool';
import filterProperties from '../tools/index.js';
import { instructions } from '../utils/conversation_config.js';
import './ConsolePage.scss';
import ImageUploadModal from './modals/ImageUploadModal';
import PayloadPreviewModal from './modals/PayloadPreviewModal';
import AreaSelectionModal from './modals/AreaSelectionModal';
import TranscriptModal from './modals/TranscriptModal';


export function ConsolePage() {
    const LOCAL_RELAY_SERVER_URL =
        process.env.REACT_APP_LOCAL_RELAY_SERVER_URL || '';

    const wavRecorderRef = useRef<WavRecorder>(
        new WavRecorder({ sampleRate: 24000 })
    );
    const wavStreamPlayerRef = useRef<WavStreamPlayer>(
        new WavStreamPlayer({ sampleRate: 24000 })
    );
    const clientRef = useRef<RealtimeClient>(
        new RealtimeClient({ url: LOCAL_RELAY_SERVER_URL })
    );

    const eventsScrollHeightRef = useRef(0);
    const eventsScrollRef = useRef<HTMLDivElement>(null);
    const startTimeRef = useRef<string>(new Date().toISOString());

    const [items, setItems] = useState<ItemType[]>([]);
    const [realtimeEvents, setRealtimeEvents] = useState<RealtimeEvent[]>([]);
    const [status, setStatus] = useState<'idle' | 'recording'>('idle');
    const [isConnected, setIsConnected] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [viewFull, setViewFull] = useState(false);
    const [listingPayload, setListingPayload] = useState<any | null>(null);
    const listingPayloadRef = useRef(listingPayload);
    const listingFlowSessionIdRef = useRef(0);
    const [currentListingQuestion, setCurrentListingQuestion] = useState<string | null>(null);
    const [filter, setFilter] = useState<any>({});
    const [toTOp, setToTop] = useState(false);
    const [load, setLoad] = useState(false);
    const [isImageUploadModalOpen, setImageUploadModalOpen] = useState(false);
    const [isPayloadPreviewOpen, setPayloadPreviewOpen] = useState(false);
    const [isAreaSelectionModalOpen, setAreaSelectionModalOpen] = useState(false);
    const [areaOptions, setAreaOptions] = useState<any[]>([]);
    const [areaSearchQuery, setAreaSearchQuery] = useState<string>('');
    const [propertyImage, setPropertyImage] = useState<string | null>(null);
    const toolsRegisteredRef = useRef(false);
    const [isTranscriptModalOpen, setTranscriptModalOpen] = useState(false);


    const ensureMic = async (): Promise<boolean> => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            stream.getTracks().forEach(t => t.stop()); // release immediately
            return true;
        } catch (err) {
            alert(
                'We could not access your microphone. Please follow the steps below to re-enable it:\n\n' +
                'iPhone / iPad (Safari/Chrome)\n' +
                'Settings > Apps > Safari/Chrome > Microphone > Allow' +
                '\n\nAndroid (Chrome)\n' +
                'Settings > Apps > Chrome > Permissions > Microphone > Allow'
            );
            return false;
        }
    };

    const ensureConnected = async () => {
        const client = clientRef.current;
        if (!client || client.isConnected()) return;

        if (!LOCAL_RELAY_SERVER_URL) {
            console.error('Missing REACT_APP_LOCAL_RELAY_SERVER_URL');
            alert('Server URL is not configured.');
            return;
        }

        await client.connect();
        setIsConnected(true);
    };

    const connectConversation = useCallback(async () => {
        setIsLoading(true);
        try {
            const micGranted = await ensureMic();
            if (!micGranted) return;

            const client = clientRef.current;
            const wavRecorder = wavRecorderRef.current;
            const wavStreamPlayer = wavStreamPlayerRef.current;

            startTimeRef.current = new Date().toISOString();
            setRealtimeEvents([]);
            setItems(client.conversation.getItems());

            await wavRecorder.begin();
            await wavStreamPlayer.connect();

            await ensureConnected();

            // if (client.getTurnDetectionType() === 'server_vad') {
            //     if (wavRecorder.getStatus() === 'recording') await wavRecorder.pause();
            //     await wavRecorder.record((data) => client.appendInputAudio(data.mono));
            // }
        } catch (setupErr) {
            console.error('connectConversation error:', setupErr);
        } finally {
            setIsLoading(false);
        }
    }, [LOCAL_RELAY_SERVER_URL]);

    useEffect(() => {
        const setup = async () => {
            await connectConversation();
        };
        setup();
    }, []);

    /**
     * Disconnect and reset conversation state
     */
    const disconnectConversation = useCallback(async () => {
        setIsLoading(true);
        setRealtimeEvents([]);
        setItems([]);
        try {
            const client = clientRef.current;
            client.disconnect();

            const wavRecorder = wavRecorderRef.current;
            if (wavRecorder.getStatus() === 'recording') {
                await wavRecorder.pause();
            }
            if (wavRecorder.getStatus() !== 'ended') {
                await wavRecorder.end();
            }
            const wavStreamPlayer = wavStreamPlayerRef.current;
            await wavStreamPlayer.interrupt();
        } finally {
            setIsConnected(false);
            setIsLoading(false);
        }
    }, []);

    const deleteConversationItem = useCallback(async (id: string) => {
        const client = clientRef.current;
        client.deleteItem(id);
    }, []);

    /**
     * start recording
     * .appendInputAudio() for each sample
     */
    const startRecording = async () => {
        const client = clientRef.current;
        console.log({
            client,
            isConnected: client?.isConnected(),
            sessionCreated: client?.sessionCreated,
        });
        try {
            if (!client || !client.isConnected()) {
                console.log('Client not connected, attempting to reconnect...');
                alert('Connection lost. Press OK to try reconnecting.');
                await disconnectConversation();
                await connectConversation();
                return;
            }

            const wavRecorder = wavRecorderRef.current;
            const wavStreamPlayer = wavStreamPlayerRef.current;
            setIsRecording(true);
            const trackSampleOffset = await wavStreamPlayer.interrupt();
            if (trackSampleOffset?.trackId) {
                const { trackId, offset } = trackSampleOffset;
                await client.cancelResponse(trackId, offset);
            }

            if (wavRecorder.getStatus() === 'recording') {
                await wavRecorder.pause();
            }
            await wavRecorder.record((data) => {
                if (!client || !client.isConnected()) return;
                client.appendInputAudio(data.mono);
            });
        } catch (error) {
            console.error(error);
            alert('Please allow microphone access again to continue.');
            setStatus('idle');
            await disconnectConversation();
            await connectConversation();
        }
    };

    const stopRecording = async (send: boolean) => {
        const client = clientRef.current;

        const wavRecorder = wavRecorderRef.current;
        if (wavRecorder.getStatus() === 'recording') {
            await wavRecorder.pause();
        }

        setIsRecording(false);
        if (send) {
            await client.createResponse();
            setTimeout(() => {
                const grid = document.querySelector<HTMLDivElement>('.properties-grid');
                if (grid) grid.scrollTop = 0;
            }, 0);
        }
    };

    useEffect(() => {
        if (eventsScrollRef.current) {
            const eventsEl = eventsScrollRef.current;
            const scrollHeight = eventsEl.scrollHeight;
            if (scrollHeight !== eventsScrollHeightRef.current) {
                eventsEl.scrollTop = scrollHeight;
                eventsScrollHeightRef.current = scrollHeight;
            }
        }
    }, [realtimeEvents]);

    //========================================================================

    const propertiesGridRef = useRef<HTMLDivElement | null>(null);

    const [investmentFilter, setInvestmentFilter] = useState<any>();
    const [topInvestments, setTopInvestments] = useState<any>({
        plans: [],
        count: 0,
        currentCount: 0,
    });
    const [topProperties, setTopProperties] = useState<any>({
        properties: [],
        count: 0,
        currentCount: 0,
    });

    const [view, setView] = useState('products');

    useEffect(() => {
        if (topProperties?.properties?.length > 0) {
            setViewFull(true);
        } else {
            setViewFull(false);
        }
    }, [topProperties?.properties]);

    useEffect(() => {
        const container = propertiesGridRef.current;
        if (container) {
            // scroll the container back to the very top
            container.scrollTop = 0;
        }
    }, [toTOp]);
    //========================================================================

    useEffect(() => {
        const conversationEls = [].slice.call(
            document.body.querySelectorAll('[data-conversation-content]')
        );
        for (const el of conversationEls) {
            const conversationEl = el as HTMLDivElement;
            conversationEl.scrollTop = conversationEl.scrollHeight;
        }
    }, [items]);

    useEffect(() => {
        // This effect runs whenever the listingPayload changes
        if (listingPayload) {
            // If payload exists, the flow is active, so open the modal
            setTranscriptModalOpen(true);
        }
        // else {
        //     // If payload is null, the flow has ended, so close it
        //     setTranscriptModalOpen(false);
        // }
    }, [listingPayload]);

    useEffect(() => {
        const client = clientRef.current;
        const wavRecorder = wavRecorderRef.current;
        console.log({ sessionCreated: client.sessionCreated });
        const wavStreamPlayer = wavStreamPlayerRef.current;

        client.updateSession();

        client.updateSession({
            instructions: instructions, voice: 'ash',
            input_audio_transcription: { model: 'gpt-4o-transcribe' },
            turn_detection: null
        });

        if (!toolsRegisteredRef.current) {
            registerGetPropertyTool(client, {
                setFilter,
                setToTop,
                setTopInvestments,
                setTopProperties,
                filterProperties,
            });

            registerGetInvestmentTool(client, {
                setInvestmentFilter,
                setTopProperties,
                setView,
                setTopInvestments,
                filterInvestments,
            });

            registerManagePropertyListingTool(client, {
                listingPayloadRef,
                listingFlowSessionIdRef,
                setListingPayload,
                setCurrentListingQuestion,
                setImageUploadModalOpen,
                setPayloadPreviewOpen,
                setAreaSelectionModalOpen,
                setAreaOptions,
                setAreaSearchQuery,
            });

            toolsRegisteredRef.current = true;
        }

        const onDisconnected = async () => {
            console.log('Client disconnected.');
            setIsConnected(false);
            if (wavRecorder.getStatus() === 'recording') {
                await wavRecorder.pause();
                setIsRecording(false);
            }
        };

        const onError = (errorEvent: any) => {
            console.error('RealtimeClient error:', JSON.stringify(errorEvent, null, 2));
            // You could branch on errorEvent.error.code for retry/backoff.
            onDisconnected();
        };

        const onRealtimeEvent = (realtimeEvent: RealtimeEvent) => {
            setRealtimeEvents((realtimeEvents) => {
                const lastEvent = realtimeEvents[realtimeEvents.length - 1];
                if (lastEvent?.event.type === realtimeEvent.event.type) {
                    // if we receive multiple events in a row, aggregate them for display purposes
                    lastEvent.count = (lastEvent.count || 0) + 1;
                    return realtimeEvents.slice(0, -1).concat(lastEvent);
                } else {
                    return realtimeEvents.concat(realtimeEvent);
                }
            });
        };

        const onInterrupted = async () => {
            const trackSampleOffset = await wavStreamPlayer.interrupt();
            if (trackSampleOffset?.trackId) {
                const { trackId, offset } = trackSampleOffset;
                await client.cancelResponse(trackId, offset);
            }
        };

        const onUpdated = async ({ item, delta, ...rest }: any) => {
            const transcript = item?.formatted?.transcript?.trim();
            if (item?.role === 'user' && item?.formatted?.transcript) {
                if (!transcript) {
                    client.sendUserMessageContent([
                        { type: 'input_text', text: 'آپ کیا کہنا چاہتے ہیں؟' }
                    ]);
                    return;
                }
            }

            const items = client.conversation.getItems();
            if (item?.role === 'assistant' && item?.formatted?.audio) {
                wavStreamPlayer.add16BitPCM(item?.formatted?.audio, item.id);
            }
            if (item?.status === 'completed' && item?.formatted?.audio?.length) {
                const wavFile = await WavRecorder.decode(
                    item.formatted.audio,
                    24000,
                    24000
                );
                item.formatted.file = wavFile;
            }
            setToTop(false);
            setItems(items);
        };

        setItems(client.conversation.getItems());

        client.on('realtime.disconnected', onDisconnected);
        client.on('error', onError);
        client.on('realtime.event', onRealtimeEvent);
        client.on('conversation.interrupted', onInterrupted);
        client.on('conversation.updated', onUpdated);

        return () => {
            client.off('realtime.disconnected', onDisconnected);
            client.off('error', onError);
            client.off('realtime.event', onRealtimeEvent);
            client.off('conversation.interrupted', onInterrupted);
            client.off('conversation.updated', onUpdated);
        };

    }, []);

    function capitalizeFirstLetter(str: string) {
        return str.charAt(0).toUpperCase() + str.slice(1);
    }

    useEffect(() => {
        listingPayloadRef.current = listingPayload;
    }, [listingPayload]);

    const [hasMore, setHasMore] = useState(true);

    const fetchMoreData = async () => {
        console.log({
            currentCount: topProperties?.currentCount,
            count: topProperties?.count,
        });
        setLoad(true);
        if (topProperties?.currentCount >= topProperties?.count) {
            setHasMore(false);
            return;
        }
        const data = {
            ...filter,
            page: filter?.page + 1,
        };
        setFilter(data);
        const filterRes: any = await filterProperties(data);
        const { properties, count } = filterRes;
        setTopProperties((prev: any) => ({
            properties: [...prev.properties, ...properties],
            count,
            currentCount: prev?.currentCount + properties?.length,
        }));

        setLoad(false);
    };


    const [selectedProjects, setSelectedProjects] = useState<ProjectType[]>([]);
    const [selectedProductInfo, setSelectedProductInfo] = useState<{
        summary?: string;
        highlights?: string[];
    }>({});

    const isAnyModalOpen = isImageUploadModalOpen || isPayloadPreviewOpen || isAreaSelectionModalOpen || isTranscriptModalOpen;

    const handleCardClick = (product: any) => {
        const matchedProjects = filterProjects(product?.applicable_projects);

        setSelectedProjects(matchedProjects);
        setSelectedProductInfo({
            summary: product?.summary,
            highlights: product?.highlights || [],
        });

        setView('projects');
    };

    const uploadImageToGraana = async (imageBase64: string): Promise<number | null> => {
        try {
            // Convert base64 to blob
            const fetchResponse = await fetch(imageBase64);
            const blob = await fetchResponse.blob();

            // Create FormData
            const formData = new FormData();
            formData.append('files[]', blob, 'property-image.jpg');

            const response = await fetch('https://www.graana.com/data/images', {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${ response.status }`);
            }

            const result = await response.json();

            if (result.success && result.data && result.data.length > 0) {
                console.log('Image upload successful:', result);
                return result.data[0].id;
            } else {
                console.error('Image upload response does not contain expected data:', result);
                return null;
            }
        } catch (error) {
            console.error('Error uploading image to Graana:', error);
            return null;
        }
    };

    const handleAreaSelection = (selectedArea: any) => {
        const coords = typeof selectedArea.lat === 'number' && typeof selectedArea.lng === 'number'
            ? { lat: selectedArea.lat, lng: selectedArea.lng }
            : undefined;

        // Step 1: Update the local state immediately for a responsive UI
        const newPayload = {
            ...(listingPayloadRef.current || {}), // Build from the most recent ref
            area: selectedArea.name,
            areaId: selectedArea.areaId,
            areaCoordinates: coords,
        };

        listingPayloadRef.current = newPayload;
        setListingPayload(newPayload);

        console.log('Area selected and local state updated:', selectedArea);

        // Step 2: Send the selected area name to the AI as if the user spoke it.
        // The AI will then process this new information and call the tool itself.
        if (clientRef.current) {
            clientRef.current.sendUserMessageContent([
                {
                    type: 'input_text',
                    text: selectedArea.name
                },
            ]);
        }

        // Step 3: Clean up the modal UI
        setAreaSelectionModalOpen(false);
        setAreaOptions([]);
        setAreaSearchQuery('');
    };

    const handleImageUpload = async (images: string[]): Promise<void> => {
        // Note: The setPropertyImage state seems to be for a single image.
        // You can remove it or adapt it to store an array if needed for other UI purposes.
        // setPropertyImage(images[0]); // For example, just show the first image elsewhere

        try {
            // Create an array of promises, one for each image upload
            const uploadPromises = images.map(image => uploadImageToGraana(image));

            // Wait for all uploads to complete using Promise.all
            const imageIds = await Promise.all(uploadPromises);

            // Filter out any nulls that may result from failed uploads
            const successfulIds = imageIds.filter((id): id is number => id !== null);

            if (successfulIds.length > 0) {
                // Append the new, successful image IDs to the payload
                setListingPayload((prev: any) => {
                    if (!prev) return prev;
                    const existingIds = prev.imageIds || [];
                    return {
                        ...prev,
                        imageIds: [...existingIds, ...successfulIds],
                    };
                });

                console.log(`${successfulIds.length} image(s) uploaded successfully with IDs:`, successfulIds);

                // Notify the AI that the images are uploaded so it can proceed
                if (clientRef.current) {
                    clientRef.current.sendUserMessageContent([
                        {
                            type: 'input_text',
                            // Use plural form in the message to the AI
                            text: 'تصاویر کامیابی سے اپلوڈ ہو گئی ہیں' // "Images have been successfully uploaded"
                        },
                    ]);
                }
            }

            if (successfulIds.length < images.length) {
                // Optionally, inform the user if some uploads failed
                alert(`${images.length - successfulIds.length} image(s) could not be uploaded.`);
            }

        } catch (error) {
            console.error('Error during multi-image upload:', error);
            if (clientRef.current) {
                clientRef.current.sendUserMessageContent([
                    {
                        type: 'input_text',
                        text: 'تصاویر اپلوڈ نہیں ہو سکیں، لیکن آگے بڑھیں' // "Couldn't upload images, but proceeding"
                    },
                ]);
            }
            // Re-throw the error to be caught by the modal's internal error handling
            throw error;
        }
    };

    return (
        <div data-component='ConsolePage'>
            { topProperties?.properties?.length ? (
                <img className='icon-image' src='/icon.png'/>
            ) : (
                <img className='logo-image' src='/logo.png'/>
            ) }
            

            {/* Payload Preview Modal */ }
            <AnimatePresence>
                { listingPayload && (
                    <PayloadPreviewModal
                        isOpen={ isPayloadPreviewOpen }
                        onClose={ () => setPayloadPreviewOpen(false) }
                        payload={ listingPayload }
                    />
                ) }
            </AnimatePresence>

            {/* Floating button to toggle payload preview */ }
            { listingPayload && (
                <button
                    className='preview-toggle-button'
                    onClick={ () => setPayloadPreviewOpen(!isPayloadPreviewOpen) }
                    title='Show listing preview'
                >
                    <Eye size={ 22 }/>
                </button>
            ) }

            {/* Image Upload Modal */ }
            <ImageUploadModal
                isOpen={ isImageUploadModalOpen }
                onClose={ () => {
                    setImageUploadModalOpen(false);
                } }
                onUpload={ handleImageUpload }
            />

            <AreaSelectionModal
                isOpen={ isAreaSelectionModalOpen }
                onClose={ () => {
                    setAreaSelectionModalOpen(false);
                    setAreaOptions([]);
                    setAreaSearchQuery('');
                } }
                onSelect={ handleAreaSelection }
                areas={ areaOptions }
                searchQuery={ areaSearchQuery }
            />

            <div className='content-main'>
                <div
                    className={ `grid-box-property ${
                        viewFull
                            ? 'cal-height'
                            : topInvestments?.plans?.length
                                ? 'cal-height'
                                : isRecording
                                    ? 'rec-height'
                                    : 'grid-box-height'
                    }` }
                >
                    { topProperties?.properties?.length === 0 &&
                        topInvestments?.plans.length === 0 && (
                            <h1
                                style={ {
                                    color: 'gray',
                                    textAlign: 'center',
                                    marginTop: '2rem',
                                } }
                            >
                                How can I help{ ' ' }
                                <span style={ { color: 'rgba(218, 41, 90, 1)' } }>You</span>?
                            </h1>
                        ) }

                    <PropertySection
                        topProperties={ topProperties }
                        hasMore={ topProperties.currentCount < topProperties.count }
                        fetchMore={ fetchMoreData }
                    />

                    <InvestmentPlans
                        topInvestments={ topInvestments }
                        view={ view }
                        setView={ setView }
                        selectedProjects={ selectedProjects }
                        selectedProductInfo={ selectedProductInfo }
                        handleCardClick={ handleCardClick }
                    />

                    <div className='fade-bottom'/>
                    <RecordingSection
                        status={ status }
                        setStatus={ setStatus }
                        isConnected={ isConnected }
                        isLoading={ isLoading }
                        load={ load }
                        topProperties={ topProperties }
                        wavRecorderRef={ wavRecorderRef }
                        wavStreamPlayerRef={ wavStreamPlayerRef }
                        startRecording={ startRecording }
                        stopRecording={ stopRecording }
                        items={ items }
                        isAnyModalOpen={ isAnyModalOpen }
                    />
                </div>
            </div>
        </div>
    );
}